import { flip, offset, type Placement, shift, useFloating } from "@floating-ui/react-dom";
import { type Input } from "antd";
import { type InjectedFormikProps, withFormik } from "formik";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Key } from "ts-key-enum";

import { cn } from "@btnet/utilities-core";

import yup from "utilities/form/yup";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTForm, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    getDOMPositionOfSelection,
    getLinkAtCursor,
    getPositionOfNodeAtCursor,
    getSelectedText,
    getTextAtCursor,
    hasSelection,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

import "./LinkEditOverlay.less";

interface ILinkEditOverlayProps {
    editor: IEditor;
    editorSize?: EditorControlBreakpointNames | null;
    fallbackPlacements?: Placement[];
}

const defaultFallbackPlacements: Placement[] = ["right-start", "left-start"];
export const LinkEditOverlay: React.FC<ILinkEditOverlayProps> = ({
    editor,
    editorSize = null,
    fallbackPlacements = defaultFallbackPlacements,
}) => {
    const { tiptapEditor, editorState, editorStateDispatch } = editor;
    const smallFormBreakpoints: EditorControlBreakpointNames[] = ["xs", "sm", "md"];
    const isSmallFormFactor = editorSize && smallFormBreakpoints.includes(editorSize);

    const { refs, floatingStyles } = useFloating({
        middleware: [
            flip({
                fallbackPlacements: fallbackPlacements,
            }),
            shift({ padding: 5 }),
            offset(5),
        ],
    });

    const handleCalculatePosition = useCallback(() => {
        const controls = editor.tiptapEditor.view.dom
            .closest(".CommentJsonContainer")
            ?.querySelector(".BTEditorChatInterface--ControlBarHeader--Controls");
        if (controls) {
            return controls.getBoundingClientRect();
        }
        return getDOMPositionOfSelection(tiptapEditor);
    }, [editor.tiptapEditor.view.dom, tiptapEditor]);
    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: handleCalculatePosition });
    }, [handleCalculatePosition, refs]);

    useEffect(() => {
        const update = () => {
            refs.setReference({ getBoundingClientRect: handleCalculatePosition });
        };
        tiptapEditor.on("selectionUpdate", update);
        return () => {
            tiptapEditor.off("selectionUpdate", update);
        };
    }, [tiptapEditor, handleCalculatePosition, refs]);

    const handleHideEditor = useCallback(() => {
        editorStateDispatch({ type: "setShowLinkEditorOverlay", payload: false });
    }, [editorStateDispatch]);

    const handleClick = useCallback(
        (event: MouseEvent) => {
            if (
                editorState.showLinkEditor &&
                refs.floating.current &&
                !refs.floating.current.contains(event.target as Node)
            ) {
                handleHideEditor();
            }
        },
        [editorState.showLinkEditor, handleHideEditor, refs.floating],
    );

    useEffect(() => {
        if (editorState.showLinkEditor) {
            window.addEventListener("mousedown", handleClick);
            window.addEventListener("scroll", handleHideEditor);
            return () => {
                window.removeEventListener("mousedown", handleClick);
                window.removeEventListener("scroll", handleHideEditor);
            };
        } else {
            return () => {};
        }
    }, [
        tiptapEditor,
        handleClick,
        handleHideEditor,
        editorState.showLinkEditor,
        isSmallFormFactor,
    ]);

    if (!editorState.showLinkEditor) {
        return null;
    }

    return (
        <div
            ref={refs.setFloating}
            className={cn("LinkEditOverlay", { SmallFormFactor: isSmallFormFactor })}
            style={floatingStyles}
        >
            <EditLinkForm editor={editor} onPressEscape={handleHideEditor} />
        </div>
    );
};

interface ILinkFormProps {
    editor: IEditor;
    onPressEscape(refocusEditor: boolean): void;
}

interface ILinkOverlayFormValues {
    url: string;
    textToDisplay?: string;
}

const EditLinkFormInternal: React.FC<
    InjectedFormikProps<ILinkFormProps, ILinkOverlayFormValues>
> = ({ values, setFieldValue, setFieldTouched, handleSubmit, onPressEscape }) => {
    const urlInputRef = useRef<Input>(null);

    useEffect(() => {
        urlInputRef.current?.focus({ preventScroll: true });
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === Key.Escape) {
            onPressEscape(true);
        }

        if (event.key === Key.Enter && !event.repeat) {
            handleSubmit();
        }
    };
    return (
        <BTForm onSubmit={handleSubmit}>
            <BTFormItemAutomatic<ILinkOverlayFormValues> id="url">
                <BTInput
                    id="url"
                    data-testid="url"
                    value={values.url}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    fieldRef={urlInputRef}
                    debouncingContext={{ useDebouncing: false }}
                    autoComplete="off"
                    onKeyDown={handleKeyDown}
                />
            </BTFormItemAutomatic>
            <BTFormItemAutomatic<ILinkOverlayFormValues> id="textToDisplay">
                <BTInput
                    id="textToDisplay"
                    data-testid="textToDisplay"
                    value={values.textToDisplay}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    debouncingContext={{ useDebouncing: false }}
                    autoComplete="off"
                    onKeyDown={handleKeyDown}
                />
            </BTFormItemAutomatic>
            <BTRow justify="end">
                <BTCol>
                    <BTButtonDeprecated
                        id="saveLink"
                        data-testid="saveLink"
                        type="primary"
                        hotkey="save"
                        onClick={handleSubmit}
                    >
                        Insert
                    </BTButtonDeprecated>
                </BTCol>
            </BTRow>
        </BTForm>
    );
};

const LinkValidators = {
    url: yup.string().url().required().label("URL"),
    textToDisplay: yup.string().label("Text to Display"),
};
const LinkSchema = yup.object().shape<ILinkOverlayFormValues>(LinkValidators);

const EditLinkForm = withFormik<ILinkFormProps, ILinkOverlayFormValues>({
    // use the default values we specified above
    mapPropsToValues: ({ editor }) => {
        const { tiptapEditor } = editor;
        const selectedText = getSelectedText(tiptapEditor);
        const linkAtCursor = getLinkAtCursor(tiptapEditor);
        const textAtCursor = getTextAtCursor(tiptapEditor);

        if (!linkAtCursor) {
            return {
                url: LinkValidators.url.isValidSync(selectedText) ? selectedText : "https://",
                textToDisplay: selectedText ?? "",
            };
        }

        return {
            url: linkAtCursor ?? "",
            textToDisplay: textAtCursor ?? "",
        };
    },

    // use the yup validation object we specified above
    validationSchema: LinkSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,

    handleSubmit: async (values: ILinkOverlayFormValues, { props, setSubmitting }) => {
        try {
            setSubmitting(true);
            const { tiptapEditor } = props.editor;
            const linkAtCursor = getLinkAtCursor(tiptapEditor);
            const isHighlighting = hasSelection(tiptapEditor);

            if (!linkAtCursor && !isHighlighting) {
                tiptapEditor
                    .chain()
                    .focus()
                    .setLink({ href: values.url, target: "_blank" })
                    .insertContent(values.textToDisplay || values.url)
                    .run();
            } else if (!linkAtCursor) {
                tiptapEditor
                    .chain()
                    .focus()
                    .deleteSelection()
                    .setLink({ href: values.url, target: "_blank" })
                    .insertContent(values.textToDisplay || values.url)
                    .run();
            } else {
                const nodePosition = getPositionOfNodeAtCursor(tiptapEditor);
                if (nodePosition) {
                    const { from, to } = nodePosition;
                    tiptapEditor
                        .chain()
                        .focus()
                        .deleteRange({
                            from,
                            to,
                        })
                        .setLink({ href: values.url, target: "_blank" })
                        .insertContent(values.textToDisplay || values.url)
                        .run();
                }
            }
        } finally {
            props.editor.editorStateDispatch({ type: "setShowLinkEditorOverlay", payload: false });
            setSubmitting(false);
        }
    },
})(EditLinkFormInternal);
